import { memo, useLayoutEffect, useMemo, useRef } from 'react'
import type { ZoneProps } from '../../data/types'
import { timeCursor } from '../../../app/time/cursor'
import { frameAtCursor, useFrameBinding, writeText } from '../../shell/bindings'
import { goNow } from '../../shell/actions'
import { WxGlyph } from '../../icons/WxIcon'
import { focusAfterLayout } from '../../../app/lib/focusTrap'
import { useReducedMotion } from '../../../app/hooks'
import { getPanorama, finite, HOUR } from './geometry'
import { InfoDisclosure } from '../../components/InfoDisclosure'
import './panorama.css'

export const Panorama = memo(function Panorama({ data }: ZoneProps) {
  const ref = useRef<HTMLElement>(null)
  const plot = useRef<HTMLDivElement>(null)
  const metrics = useRef({ left: 0, width: 0, temperatureHeight: 0 })
  const scrubPosition = useRef<number | null>(null)
  const readoutMotion = useRef<Animation | undefined>(undefined)
  const readoutFrame = useRef(frameAtCursor(data))
  const reduced = useReducedMotion()
  useLayoutEffect(() => { readoutFrame.current = frameAtCursor(data); return () => readoutMotion.current?.cancel() }, [data, reduced])
  const resized = useRef(() => {})
  const chart = useMemo(() => getPanorama(data.models?.hours ?? [], data.report, data.units), [data.models, data.report, data.units])
  const binding = useMemo(() => (node: HTMLElement) => {
    const reading = node.querySelector('[data-hour-reading]'), detail = node.querySelector('[data-hour-detail]')
    const readout = node.querySelector<HTMLElement>('.v5-time-readout')
    const cursor = node.querySelector<HTMLElement>('[data-hour-cursor]'), dot = node.querySelector<HTMLElement>('[data-hour-dot]')
    const slider = node.querySelector<HTMLInputElement>('input')
    const samples = new Map(chart.samples.map((s) => [s.hour.index, s]))
    const nowReading = `Rain ${data.models?.hours[0]?.precip ?? '—'} · chance ${data.models?.hours[0]?.chance ?? '—'} · wind ${data.now?.wind ?? '—'}`
    return (frame: NonNullable<ZoneProps['data']['now']>) => {
      const sample = samples.get(frame.index)
      const inside = !!sample && frame.time >= chart.start && frame.time < chart.end + HOUR
      writeText(reading, `${frame.stamp} · ${frame.temperature} · ${frame.description}`)
      writeText(detail, inside ? timeCursor.isNow() ? nowReading : sample.readout : 'Selected time is beyond this 48-hour chart.')
      if (readoutFrame.current !== frame && !reduced) {
        readoutMotion.current?.cancel()
        readoutMotion.current = readout?.animate?.([{ opacity: .9 }, { opacity: 1 }], { duration: 90, easing: 'ease-out' })
      }
      readoutFrame.current = frame
      if (cursor) {
        cursor.style.transform = `translate3d(${inside ? scrubPosition.current ?? sample.x / 100 * metrics.current.width : 0}px,0,0)`
        cursor.style.opacity = inside ? '1' : '0'
      }
      if (dot) {
        dot.style.transform = `translate3d(-3px,${sample?.y != null ? sample.y * metrics.current.temperatureHeight - 3 : 0}px,0)`
        // Current observations and hourly guidance can differ: no observation dot on a forecast line.
        dot.style.opacity = inside && scrubPosition.current == null && !timeCursor.isNow() && sample.y != null ? '1' : '0'
      }
      if (slider) {
        slider.value = timeCursor.isNow() ? '0' : String(Math.min(47, Math.max(0, Math.round((frame.time - chart.start) / HOUR))))
        slider.setAttribute('aria-valuetext', `${frame.stamp}, ${frame.temperature}, ${frame.description}${inside ? '' : ', beyond the chart'}`)
      }
    }
  }, [chart, data.models, data.now, reduced])
  useFrameBinding(ref, data, binding)

  useLayoutEffect(() => {
    const node = plot.current
    if (!node) return
    const slider = ref.current?.querySelector<HTMLInputElement>('input')
    const cursor = ref.current?.querySelector<HTMLElement>('[data-hour-cursor]')
    const dot = ref.current?.querySelector<HTMLElement>('[data-hour-dot]')
    const apply = binding(ref.current!)
    resized.current = () => {
      const frame = frameAtCursor(data)
      if (frame) apply(frame)
    }
    resized.current()
    const select = (slot: number) => {
      const time = chart.start + Math.min(47, Math.max(0, Math.round(slot))) * HOUR
      if (data.models?.hourAt(time)?.t !== time) return false
      timeCursor.set(time)
      return true
    }
    let pointer: number | null = null, wheelSlot = 0
    let cancelFocus = () => {}
    let lastWheelSelection: number | null = null
    const pick = (event: PointerEvent) => {
      if (!metrics.current.width) return
      const x = Math.min(metrics.current.width, Math.max(0, event.clientX - metrics.current.left))
      if (!select(x / metrics.current.width * 47)) return
      scrubPosition.current = x
      if (cursor) cursor.style.transform = `translate3d(${x}px,0,0)`
      if (dot) dot.style.opacity = '0'
    }
    const down = (event: PointerEvent) => {
      if (event.button !== 0 || !chart.series.length) return
      event.preventDefault()
      pointer = event.pointerId; node.setPointerCapture?.(pointer)
      pick(event)
      cancelFocus()
      if (slider) cancelFocus = focusAfterLayout(slider)
    }
    const move = (event: PointerEvent) => { if (pointer === event.pointerId) pick(event) }
    const up = () => { if (pointer == null) return; pointer = null; scrubPosition.current = null; timeCursor.flush(); resized.current() }
    const wheel = (event: WheelEvent) => {
      // Horizontal trackpad travel inspects time; vertical travel remains document scrolling.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || event.ctrlKey || !chart.series.length) return
      event.preventDefault()
      // Rebase a new gesture on external key/day/pointer selection, while retaining fractional
      // wheel travel through an unselectable gap. The shared cursor remains the authority.
      if (timeCursor.selected !== lastWheelSelection) wheelSlot = timeCursor.isNow() ? 0 : (timeCursor.instant() - chart.start) / HOUR
      wheelSlot = Math.min(47, Math.max(0, wheelSlot + event.deltaX / Math.max(8, metrics.current.width / 47)))
      select(wheelSlot)
      lastWheelSelection = timeCursor.selected
    }
    node.addEventListener('pointerdown', down); node.addEventListener('pointermove', move)
    node.addEventListener('pointerup', up); node.addEventListener('pointercancel', up); node.addEventListener('lostpointercapture', up)
    node.addEventListener('wheel', wheel, { passive: false })
    return () => {
      scrubPosition.current = null
      cancelFocus()
      node.removeEventListener('pointerdown', down); node.removeEventListener('pointermove', move)
      node.removeEventListener('pointerup', up); node.removeEventListener('pointercancel', up); node.removeEventListener('lostpointercapture', up)
      node.removeEventListener('wheel', wheel)
    }
  }, [binding, chart, data])

  useLayoutEffect(() => {
    const node = plot.current!
    const temperature = node.querySelector<HTMLElement>('.v5-time-temperature')!
    const cache = () => {
      const rect = node.getBoundingClientRect()
      metrics.current = { left: rect.left, width: rect.width, temperatureHeight: temperature.getBoundingClientRect().height }
      resized.current()
    }
    // One observer measures before paint, after layout; report binds reuse its geometry.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(cache) : null
    if (observer) observer.observe(node)
    else cache()
    return () => observer?.disconnect()
  }, [])

  const latest = useRef({ data, chart }); latest.current = { data, chart }
  const events = chart.events.filter(e => !e.kind.startsWith('moon'))
  const shape = [chart.series.length, chart.nights.length, !!chart.peak, !!chart.coolest, chart.coolest === chart.peak, chart.hasTemperature, chart.hasRain, chart.hasChance, chart.hasWind, data.pending, data.units.temp, events.map(e => e.kind).join(',')].join('|')
  useLayoutEffect(() => {
    const node = ref.current!
    const attr = (element: Element | null, key: string, value: string) => { if (element?.getAttribute(key) !== value) element?.setAttribute(key, value) }
    attr(node.querySelector('[data-qa="temperature-interval"]'), 'd', chart.band)
    attr(node.querySelector('[data-qa="temperature-line"]'), 'd', chart.temperature)
    attr(node.querySelector('.v5-time-chance-path'), 'd', chart.chance)
    attr(node.querySelector('.v5-time-wind-path'), 'd', chart.wind)
    node.querySelectorAll('.v5-time-rain-bar').forEach((path, i) => {
      const h = chart.series[i]
      attr(path, 'd', finite(h.hour.precip_mm) && h.hour.precip_mm > 0 ? `M${chart.x(h.t)} 94V${94 - h.hour.precip_mm / chart.rainMax * 80}` : '')
    })
    node.querySelector<HTMLElement>('.v5-time-chart')!.toggleAttribute('data-dry', chart.dry)
    node.querySelectorAll<HTMLElement>('.v5-time-nights > i').forEach((night, i) => {
      night.style.left = `${chart.nights[i].left / 10}%`; night.style.width = `${(chart.nights[i].right - chart.nights[i].left) / 10}%`
    })
    const extremes = [chart.peak, chart.coolest].filter(h => h != null)
    node.querySelectorAll<HTMLElement>('.v5-time-anchor').forEach((anchor, i) => { const h = extremes[i]; anchor.style.left = `${chart.x(h.t) / 10}%`; anchor.style.top = `${chart.y(h.tempValue!)}%` })
    for (const [selector, h, label] of [['.v5-time-peak', chart.peak, 'Hourly peak'], ['.v5-time-coolest', chart.coolest, 'Hourly low']] as const) {
      const element = node.querySelector<HTMLElement>(selector)
      if (!element || !h) continue
      const x = chart.x(h.t)
      element.style.left = `${x / 10}%`; element.classList.toggle('at-end', x > 750); element.classList.toggle('at-start', x < 250)
      element.title = `${h.stamp} · hourly forecast ${label === 'Hourly peak' ? 'peak' : 'minimum'}`
      writeText(element.querySelector('[data-extreme-value]'), `${label} ${h.temp}`)
      writeText(element.querySelector('small'), `${h.dayName} ${h.timeFull}`)
    }
    node.querySelectorAll<HTMLElement>('.v5-time-ticks > span').forEach((tick, i) => {
      const slot = [0, 6, 12, 18, 24, 30, 36, 42, 47][i], h = chart.series.find(h => h.t === chart.start + slot * HOUR)
      writeText(tick, h ? slot === 0 ? 'Now' : h.timeFull : `+${slot}h`)
    })
    node.querySelectorAll<HTMLElement>('.v5-time-events > span').forEach((element, i) => {
      const event = events[i]
      element.style.left = `${event.x}%`; element.title = event.label; element.setAttribute('aria-label', event.label)
      element.classList.toggle('at-start', event.x < 15); element.classList.toggle('at-end', event.x > 85)
      writeText(element.querySelector('small'), event.stamp); writeText(element.querySelector('b'), event.label)
    })
    const scales = node.querySelectorAll('.v5-time-scales > span')
    const temperatures = scales[0].querySelectorAll('b')
    writeText(temperatures[0], chart.high); writeText(temperatures[1], chart.low)
    writeText(scales[1], chart.hasRain ? chart.rainLabel : '—'); writeText(scales[3], chart.hasWind ? chart.windLabel : '—')
    attr(node.querySelector('.v5-time-rain svg'), 'aria-label', `Hourly liquid precipitation, scale zero to ${chart.rainLabel}`)
    attr(node.querySelector('.v5-time-wind svg'), 'aria-label', `Wind speed, scale zero to ${chart.windLabel}`)
  }, [chart, data, shape])
  const ticks = [0, 6, 12, 18, 24, 30, 36, 42, 47].map((slot) => {
    const h = chart.series.find((h) => h.t === chart.start + slot * HOUR)
    return { slot, label: h ? slot === 0 ? 'Now' : h.timeFull : `+${slot}h` }
  })
  // A report binds geometry; React visits the graphic only when its topology changes.
  const graphic = useMemo(() => <div className="v5-time-chart" data-dry={chart.dry || undefined}>
      <div className="v5-time-labels" aria-hidden="true"><span>Temp °{data.units.temp}</span><span>Rain</span><span>Chance</span><span>Wind</span></div>
      <div ref={plot} className="v5-time-plot" data-scrub>
        <div className="v5-time-nights" aria-hidden="true">{chart.nights.map((n) => <i key={n.left} style={{ left: `${n.left / 10}%`, width: `${(n.right - n.left) / 10}%` }} />)}</div>
        <div className="v5-time-temperature">
          <svg viewBox="0 0 1000 100" preserveAspectRatio="none" role="img" aria-label={`Hourly temperature in degrees ${data.units.temp}, with supplied model intervals and gaps for missing hours`}>
            <path d="M0 15H1000 M0 50H1000 M0 85H1000" className="v5-time-grid" />
            <path d={chart.band} className="v5-time-band" data-qa="temperature-interval" /><path d={chart.temperature} className="v5-time-temp-path" data-qa="temperature-line" />
          </svg>
          {[chart.peak, chart.coolest].filter((h) => h != null).map((h, i) => <i key={i} className="v5-time-anchor" aria-hidden="true" style={{ left: `${chart.x(h.t) / 10}%`, top: `${chart.y(h.tempValue!)}%` }} />)}
          {chart.peak && <span className={`v5-time-extreme v5-time-peak ${chart.x(chart.peak.t) > 750 ? 'at-end' : chart.x(chart.peak.t) < 250 ? 'at-start' : ''}`} style={{ left: `${chart.x(chart.peak.t) / 10}%` }} title={`${chart.peak.stamp} · hourly forecast peak`}><span data-extreme-value>Hourly peak {chart.peak.temp}</span></span>}
          {chart.coolest && chart.coolest !== chart.peak && <span className={`v5-time-extreme v5-time-coolest ${chart.x(chart.coolest.t) > 750 ? 'at-end' : chart.x(chart.coolest.t) < 250 ? 'at-start' : ''}`} style={{ left: `${chart.x(chart.coolest.t) / 10}%` }} title={`${chart.coolest.stamp} · hourly forecast minimum`}><span data-extreme-value>Hourly low {chart.coolest.temp}</span><small>{chart.coolest.dayName} {chart.coolest.timeFull}</small></span>}
          {!chart.hasTemperature && <span className="v5-time-gap">{data.pending ? 'Waiting for hourly guidance' : 'Temperature not supplied'}</span>}
        </div>
        <div className="v5-time-rain"><svg viewBox="0 0 1000 100" preserveAspectRatio="none" role="img" aria-label={`Hourly liquid precipitation, scale zero to ${chart.rainLabel}`}>
          {chart.series.map((h, i) => <path key={i} d={finite(h.hour.precip_mm) && h.hour.precip_mm > 0 ? `M${chart.x(h.t)} 94V${94 - h.hour.precip_mm / chart.rainMax * 80}` : ''} className="v5-time-rain-bar" />)}
        </svg>{!chart.hasRain && <span className="v5-time-gap">Not supplied</span>}</div>
        <div className="v5-time-chance"><svg viewBox="0 0 1000 100" preserveAspectRatio="none" role="img" aria-label="Precipitation chance, zero to one hundred percent"><path d={chart.chance} className="v5-time-chance-path" /></svg>{!chart.hasChance && <span className="v5-time-gap">Not supplied</span>}</div>
        <div className="v5-time-wind"><svg viewBox="0 0 1000 100" preserveAspectRatio="none" role="img" aria-label={`Wind speed, scale zero to ${chart.windLabel}`}><path d={chart.wind} className="v5-time-wind-path" /></svg>{!chart.hasWind && <span className="v5-time-gap">Not supplied</span>}</div>
        <div className="v5-time-ticks" aria-hidden="true">{ticks.map(({ slot, label }) => <span key={slot} className={slot % 12 !== 0 && slot !== 47 ? 'v5-time-dense-tick' : ''} style={{ left: `${slot / 47 * 100}%` }}>{label}</span>)}</div>
        <div className="v5-time-events" aria-label="Sunrise and sunset">{chart.events.filter(e => !e.kind.startsWith('moon')).map((e) => <span key={e.key} data-event={e.kind} className={e.x < 15 ? 'at-start' : e.x > 85 ? 'at-end' : undefined} style={{ left: `${e.x}%` }} title={e.label} aria-label={e.label}><WxGlyph glyph={e.kind === 'sunrise' || e.kind === 'sunset' ? e.kind : 'moon-waxing-crescent'} size={14} /><small>{e.stamp}</small><b className="v5-time-sr">{e.label}</b></span>)}</div>
        <div className="v5-time-cursor" data-hour-cursor aria-hidden="true"><i data-hour-dot /></div>
        <input className="v5-time-range" type="range" min="0" max="47" defaultValue="0" step="1" aria-label="Forecast time" disabled={!chart.series.length} onInput={(event) => {
          const { data, chart } = latest.current
          const time = chart.start + Number(event.currentTarget.value) * HOUR
          if (data.models?.hourAt(time)?.t === time) { timeCursor.set(time); timeCursor.flush() }
        }} onKeyDown={(event) => {
          const { chart } = latest.current
          if (!['Home', 'End', 'n', 'N', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) || !chart.series.length) return
          event.preventDefault(); event.stopPropagation()
          const step = event.altKey ? 24 : event.shiftKey ? 6 : 1
          if (event.key === 'Home' || event.key.toLowerCase() === 'n') timeCursor.toNow()
          else if (event.key === 'End') timeCursor.set(chart.series.at(-1)!.t)
          else {
            const direction = event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1 : 1
            const current = timeCursor.isNow() ? chart.start : timeCursor.instant()
            const target = Math.min(chart.end, Math.max(chart.start, current + direction * step * HOUR))
            const hour = direction > 0 ? chart.series.find((h) => h.t >= target) : chart.series.findLast((h) => h.t <= target)
            if (hour) timeCursor.set(hour.t)
          }
          timeCursor.flush()
        }} />
      </div>
      <div className="v5-time-scales" aria-hidden="true"><span>{chart.hasTemperature ? <><b>{chart.high}</b><b>{chart.low}</b></> : '—'}</span><span>{chart.hasRain ? chart.rainLabel : '—'}</span><span>0–100%</span><span>{chart.hasWind ? chart.windLabel : '—'}</span></div>
    </div>, [shape])
  return <section ref={ref} className="v5-panorama v5-zone v5-time-panorama" data-zone="panorama" data-qa="time-panorama" id="v5-hours" aria-labelledby="v5-panorama-title">
    <div className="v5-zone-heading"><h2 id="v5-panorama-title">48-hour forecast</h2><button className="v5-text-link" onClick={goNow}>Back to now</button></div>
    <div className="v5-time-readout"><p data-hour-reading>{data.now ? `Now · ${data.now.temperature} · ${data.now.description}` : data.pending ? 'Hourly forecast loading' : 'Hourly forecast unavailable'}</p><p data-hour-detail>Rain — · chance — · wind —</p></div>
    {graphic}
    <div className="v5-time-caption"><p>{chart.dry ? 'Low rain chances in available hours. ' : ''}Drag to explore · shaded areas show night.</p><InfoDisclosure label="Reading the 48-hour chart"><p>{chart.band ? 'The band shows the supplied model range, not calibrated odds.' : 'A model range was not supplied.'} Missing hours stay empty.</p><p>Rain and wind use their own labelled scales. {chart.dry ? 'Available rain chances are 10% or less; this does not guarantee dry weather. ' : ''}Use the arrow keys or swipe sideways to explore; vertical swipes scroll the page.</p></InfoDisclosure></div>
  </section>
})
